import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { projectToScreen, screenToWorldOnPlane } from '../utils/measureUtils';

interface WorldPos { x: number; y: number; z: number; }

export interface ExtractionSquareData {
  center: WorldPos;
  halfSize: number;
  corners: WorldPos[];
  area: number;
  perimeter: number;
}

interface ExtractionToolProps {
  viewerRef: React.MutableRefObject<any | null>;
  isActive: boolean;
  onClose: () => void;
  onExtract: (data: ExtractionSquareData) => void;
  anchorRef?: React.RefObject<HTMLButtonElement>;
}

type Step = 'placing' | 'active';

const MIN_HALF    = 10;
const MAX_HALF    = 2000;
const DEFAULT_HALF = 50;
const RESIZE_STEP  = 10;

function pickWorldPoint(viewer: any, canvas: HTMLCanvasElement, clientX: number, clientY: number): WorldPos | null {
  const { camera, raycaster, splatMesh, getRenderDimensions } = viewer ?? {};
  if (!camera || !raycaster || !splatMesh || !getRenderDimensions) return null;
  const rect = canvas.getBoundingClientRect();
  const renderDimensions = new THREE.Vector2();
  const outHits: unknown[] = [];
  getRenderDimensions.call(viewer, renderDimensions);
  camera.updateMatrixWorld?.();
  raycaster.setFromCameraAndScreenPosition(
    camera,
    new THREE.Vector2(
      ((clientX - rect.left) / rect.width)  * renderDimensions.x,
      ((clientY - rect.top)  / rect.height) * renderDimensions.y,
    ),
    renderDimensions,
  );
  const result = raycaster.intersectSplatMesh(splatMesh, outHits);
  const hits   = Array.isArray(result) ? result : outHits;
  const origin = (hits[0] as any)?.origin;
  const x = Number(origin?.x), y = Number(origin?.y), z = Number(origin?.z);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
  return { x, y, z };
}

function computeCorners(center: WorldPos, halfSize: number): WorldPos[] {
  return [
    { x: center.x - halfSize, y: center.y, z: center.z - halfSize },
    { x: center.x + halfSize, y: center.y, z: center.z - halfSize },
    { x: center.x + halfSize, y: center.y, z: center.z + halfSize },
    { x: center.x - halfSize, y: center.y, z: center.z + halfSize },
  ];
}

function isPointInPolygon(px: number, py: number, pts: { x: number; y: number }[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y;
    const xj = pts[j].x, yj = pts[j].y;
    if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

export default function ExtractionTool({ viewerRef, isActive, onClose, onExtract, anchorRef }: ExtractionToolProps) {
  const [step, setStep]         = useState<Step>('placing');
  const [center, setCenter]     = useState<WorldPos | null>(null);
  const [halfSize, setHalfSize] = useState(DEFAULT_HALF);
  const [isDragging, setIsDragging] = useState(false);

  // Mutable refs — RAF and pointer handlers always see current values
  const stepRef       = useRef<Step>('placing');
  const centerRef     = useRef<WorldPos | null>(null);
  const halfSizeRef   = useRef(DEFAULT_HALF);
  const isDraggingRef = useRef(false);
  const dragStartWorldRef  = useRef<WorldPos | null>(null);
  const dragStartCenterRef = useRef<WorldPos | null>(null);

  useEffect(() => { stepRef.current   = step;     }, [step]);
  useEffect(() => { centerRef.current = center;   }, [center]);
  useEffect(() => { halfSizeRef.current = halfSize; }, [halfSize]);

  // Direct DOM refs for SVG — RAF mutates attributes without React re-render
  const polygonRef    = useRef<SVGPolygonElement>(null);
  const circleRefs    = useRef<(SVGCircleElement | null)[]>([null, null, null, null]);
  const centerDotRef  = useRef<SVGCircleElement>(null);
  const screenCornersRef = useRef<{ x: number; y: number; visible: boolean }[]>([]);

  // Panel anchored to the toolbar button
  const [panelRight, setPanelRight] = useState<number>(16);
  useEffect(() => {
    if (!anchorRef?.current) return;
    const update = () => {
      const rect = anchorRef!.current!.getBoundingClientRect();
      setPanelRight(window.innerWidth - rect.right);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [anchorRef, isActive]);

  // Reset every time the tool activates — ensures stale state from a previous
  // session never leaks through, regardless of how quickly it was toggled.
  useEffect(() => {
    if (isActive) {
      setStep('placing');
      setCenter(null);
      setHalfSize(DEFAULT_HALF);
      setIsDragging(false);
      // Reset mutable refs immediately so RAF/event handlers see clean values
      // before the state-sync effects run.
      isDraggingRef.current   = false;
      stepRef.current         = 'placing';
      centerRef.current       = null;
      halfSizeRef.current     = DEFAULT_HALF;
      dragStartWorldRef.current  = null;
      dragStartCenterRef.current = null;
    }
  }, [isActive]);

  // Freeze viewer controls while tool is active
  useEffect(() => {
    if (!isActive) return;
    const viewer = viewerRef.current;
    if (!viewer) return;
    const prevMovement = viewer.controls?.movementSpeed ?? 5;
    const prevLook     = viewer.controls?.lookSpeed     ?? 10;
    const prevMaxClick = viewer.maxClickToMoveDistance  ?? 15;
    if (viewer.controls) {
      viewer.controls.movementSpeed = step === 'active' ? 0.3 : 0;
      viewer.controls.lookSpeed     = 0;
    }
    viewer.maxClickToMoveDistance  = 0;
    viewer.clickTransitioning      = false;
    viewer.transitioningCameraTarget = false;
    return () => {
      const v = viewerRef.current;
      if (!v || v !== viewer) return;
      if (v.controls) {
        v.controls.movementSpeed = prevMovement;
        v.controls.lookSpeed     = prevLook;
      }
      v.maxClickToMoveDistance     = prevMaxClick;
      v.clickTransitioning         = false;
      v.transitioningCameraTarget  = false;
    };
  }, [step, isActive, viewerRef]);

  // RAF loop — write SVG attributes directly, no React state per frame
  useEffect(() => {
    if (step !== 'active' || !center) return;
    let rafId: number;
    const loop = () => {
      const viewer = viewerRef.current;
      const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
      if (viewer?.camera && canvas) {
        const c  = centerRef.current!;
        const hs = halfSizeRef.current;
        const pts = computeCorners(c, hs).map(corner => projectToScreen(corner, viewer.camera, canvas));
        screenCornersRef.current = pts;
        // Always draw — off-screen corners are fine (SVG clips naturally).
        // Only skip if a corner projects to an unreasonable coordinate (behind camera).
        const W = canvas.clientWidth, H = canvas.clientHeight;
        const reasonable = (p: { x: number; y: number }) => Math.abs(p.x) < W * 10 && Math.abs(p.y) < H * 10;
        if (pts.every(reasonable)) {
          polygonRef.current?.setAttribute('points', pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '));
          pts.forEach((p, i) => {
            circleRefs.current[i]?.setAttribute('cx', p.x.toFixed(1));
            circleRefs.current[i]?.setAttribute('cy', p.y.toFixed(1));
            circleRefs.current[i]?.setAttribute('display', p.visible ? '' : 'none');
          });
        }
        const cScreen = projectToScreen(c, viewer.camera, canvas);
        if (reasonable(cScreen)) {
          centerDotRef.current?.setAttribute('cx', cScreen.x.toFixed(1));
          centerDotRef.current?.setAttribute('cy', cScreen.y.toFixed(1));
          centerDotRef.current?.setAttribute('display', cScreen.visible ? '' : 'none');
        }
      }
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, [step, center, viewerRef]);

  // Click to place center
  useEffect(() => {
    if (!isActive || step !== 'placing') return;
    const viewer = viewerRef.current;
    const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
    if (!canvas) return;
    const handler = (e: MouseEvent) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      const wp = pickWorldPoint(viewer, canvas, e.clientX, e.clientY);
      if (!wp) return;
      setCenter(wp);
      setStep('active');
    };
    canvas.addEventListener('click', handler, true);
    return () => canvas.removeEventListener('click', handler, true);
  }, [isActive, step, viewerRef]);

  // Drag to move entire square
  useEffect(() => {
    if (!isActive || step !== 'active') return;
    const viewer = viewerRef.current;
    const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
    if (!canvas) return;

    const onPointerDown = (e: PointerEvent) => {
      if (isDraggingRef.current) return;
      const pts = screenCornersRef.current;
      if (pts.length < 4 || !pts.every(p => p.visible)) return;
      if (!isPointInPolygon(e.clientX, e.clientY, pts)) return;

      const camera = viewer?.camera;
      if (!camera || !centerRef.current) return;
      const worldAtCursor = screenToWorldOnPlane(e.clientX, e.clientY, camera, canvas, centerRef.current.y);
      if (!worldAtCursor) return;

      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

      isDraggingRef.current     = true;
      setIsDragging(true);
      dragStartWorldRef.current  = { x: worldAtCursor.x, y: worldAtCursor.y, z: worldAtCursor.z };
      dragStartCenterRef.current = { ...centerRef.current };
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDraggingRef.current) return;
      const camera = viewer?.camera;
      if (!camera || !dragStartWorldRef.current || !dragStartCenterRef.current) return;
      const worldAtCursor = screenToWorldOnPlane(e.clientX, e.clientY, camera, canvas, dragStartCenterRef.current.y);
      if (!worldAtCursor) return;
      const newCenter: WorldPos = {
        x: dragStartCenterRef.current.x + (worldAtCursor.x - dragStartWorldRef.current.x),
        y: dragStartCenterRef.current.y,
        z: dragStartCenterRef.current.z + (worldAtCursor.z - dragStartWorldRef.current.z),
      };
      centerRef.current = newCenter;
      setCenter(newCenter);
    };

    const onPointerUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        setIsDragging(false);
      }
    };

    canvas.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup',   onPointerUp);
    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup',   onPointerUp);
    };
  }, [isActive, step, viewerRef]);

  // Scroll to resize
  useEffect(() => {
    if (!isActive || step !== 'active') return;
    const handler = (e: WheelEvent) => {
      if (isDraggingRef.current) return;
      e.preventDefault();
      setHalfSize(s => Math.max(MIN_HALF, Math.min(MAX_HALF, s + (e.deltaY < 0 ? RESIZE_STEP : -RESIZE_STEP))));
    };
    window.addEventListener('wheel', handler, { passive: false });
    return () => window.removeEventListener('wheel', handler);
  }, [isActive, step]);

  const handleExtract = useCallback(() => {
    if (!center) return;
    const corners = computeCorners(center, halfSize);
    const side    = halfSize * 2;
    onExtract({ center, halfSize, corners, area: side * side, perimeter: side * 4 });
  }, [center, halfSize, onExtract]);

  if (!isActive) return null;

  const sideLenM = (halfSize * 2).toFixed(0);
  const areaM2   = ((halfSize * 2) ** 2).toFixed(0);

  return (
    <>
      {/* Cursor feedback */}
      {step === 'placing' && <style>{`canvas { cursor: crosshair !important; }`}</style>}
      {step === 'active' && !isDragging && <style>{`canvas { cursor: move !important; }`}</style>}
      {isDragging && <style>{`canvas { cursor: grabbing !important; }`}</style>}

      {/* SVG overlay — pointer-events none; RAF mutates attrs */}
      {step === 'active' && center && (
        <svg style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1001 }}>
          <polygon
            ref={polygonRef}
            points=""
            fill="rgba(255,180,0,0.07)"
            stroke="rgba(255,180,0,0.9)"
            strokeWidth={2}
            strokeDasharray="10 5"
          />
          {[0, 1, 2, 3].map(i => (
            <circle
              key={i}
              ref={el => { circleRefs.current[i] = el; }}
              cx="0" cy="0" r={5}
              fill="rgba(255,180,0,0.9)"
            />
          ))}
          {/* Center dot — visual drag affordance */}
          <circle
            ref={centerDotRef}
            cx="0" cy="0" r={9}
            fill="rgba(255,180,0,0.22)"
            stroke="rgba(255,180,0,0.85)"
            strokeWidth={1.5}
          />
        </svg>
      )}

      {/* Side panel — matches MeasureTool w-64 frosted-glass card */}
      <div
        className="fixed z-[1002] w-64 rounded-2xl overflow-hidden pointer-events-none"
        style={{
          top: 68,
          right: panelRight,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
          fontFamily: "'Inter', system-ui, sans-serif",
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10" style={{ pointerEvents: 'auto' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,180,0,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="1"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          <span style={{ color: 'white', fontSize: 13, fontWeight: 600 }}>Extract Region</span>
        </div>

        {/* Placing: instruction */}
        {step === 'placing' && (
          <div className="p-4" style={{ pointerEvents: 'auto' }}>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', margin: '0 0 12px', textAlign: 'center', lineHeight: 1.5 }}>
              Click anywhere in the scene to place the extraction center…
            </p>
            <button
              onClick={onClose}
              className="w-full py-2 rounded-xl text-xs font-semibold"
              style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Active: stats + controls */}
        {step === 'active' && center && (
          <>
            {/* Stats */}
            <div className="px-3 pt-3 pb-2 space-y-2 border-b border-white/10" style={{ pointerEvents: 'auto' }}>
              <div className="rounded-xl px-3 py-2 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                  Side Length
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: 'white' }}>
                  {sideLenM}m × {sideLenM}m
                </div>
              </div>
              <div className="rounded-xl px-3 py-2 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                  Area
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>
                  {areaM2} m²
                </div>
              </div>
            </div>

            {/* Hint */}
            <div className="px-4 pt-3 pb-1" style={{ pointerEvents: 'none' }}>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: 0, lineHeight: 1.5 }}>
                Drag inside square to move · Scroll or ± to resize
              </p>
            </div>

            {/* ± resize */}
            <div className="flex gap-2 px-4 pt-2 pb-1" style={{ pointerEvents: 'auto' }}>
              <button
                onClick={() => setHalfSize(s => Math.max(MIN_HALF, s - RESIZE_STEP))}
                className="flex-1 py-2 rounded-xl font-semibold"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}
              >−</button>
              <button
                onClick={() => setHalfSize(s => Math.min(MAX_HALF, s + RESIZE_STEP))}
                className="flex-1 py-2 rounded-xl font-semibold"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}
              >+</button>
            </div>

            {/* Extract + Cancel */}
            <div className="flex flex-col gap-2 px-4 pb-4 pt-2" style={{ pointerEvents: 'auto' }}>
              <button
                onClick={handleExtract}
                className="w-full py-2 rounded-xl text-xs font-semibold"
                style={{ background: 'rgba(255,180,0,0.85)', color: 'black', border: 'none', cursor: 'pointer' }}
              >
                Extract
              </button>
              <button
                onClick={onClose}
                className="w-full py-2 rounded-xl text-xs font-semibold"
                style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
